export type VariantLabelRow = {
  id: string;
  label: string;
  sku: string;
  price: number;
  stock: number;
};

export type VariantLabelProduct = {
  id: number;
  name: string;
  sku: string;
  price: string | number;
  hasVariants?: boolean | null;
  variants?: string | null;
  categoryName?: string | null;
};

export type PrintableLabelProduct = {
  id: number;
  selectionId?: string;
  name: string;
  variantLabel?: string;
  sku: string;
  price: string | number;
  stock?: number;
  categoryName?: string | null;
};

export const MAX_LABEL_COPIES = 9999;

export function defaultLabelCopies(product: Pick<PrintableLabelProduct, "selectionId" | "stock">): number {
  if (!product.selectionId?.startsWith("variant:")) return 1;
  return Math.min(MAX_LABEL_COPIES, Math.max(0, Math.floor(product.stock ?? 0)));
}

export function parseLabelVariants(raw: string | null | undefined): VariantLabelRow[] | null {
  if (!raw) return [];
  try {
    const data: unknown = JSON.parse(raw);
    if (!Array.isArray(data)) return null;
    const ids = new Set<string>();
    return data.map(value => {
      if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Invalid variant");
      const row = value as Record<string, unknown>;
      const id = typeof row.id === "string" ? row.id.trim() : "";
      const label = typeof row.label === "string" ? row.label.trim() : "";
      const sku = typeof row.sku === "string" ? row.sku.trim() : "";
      const price = Number(row.price);
      const stock = Number(row.stock);
      if (!id || ids.has(id) || !label || row.price == null || row.stock == null ||
          !Number.isFinite(price) || price < 0 || !Number.isFinite(stock) || stock < 0) {
        throw new Error("Invalid variant");
      }
      ids.add(id);
      return { id, label, sku, price, stock };
    });
  } catch {
    return null;
  }
}

export function printableVariant(product: VariantLabelProduct, variant: VariantLabelRow): PrintableLabelProduct {
  return {
    id: product.id,
    selectionId: `variant:${product.id}:${variant.id}`,
    name: product.name,
    variantLabel: variant.label,
    sku: variant.sku.trim(),
    price: variant.price,
    stock: variant.stock,
    categoryName: product.categoryName,
  };
}

export function printableProducts(products: VariantLabelProduct[]): PrintableLabelProduct[] {
  return products.flatMap(product => {
    if (!product.hasVariants) return [product];
    const variants = parseLabelVariants(product.variants);
    return variants?.map(variant => printableVariant(product, variant)) ?? [];
  });
}

export function findBarcodeMatch<T extends VariantLabelProduct>(
  products: T[],
  code: string,
): { status: "found"; product: T; variant?: VariantLabelRow } | { status: "missing" | "ambiguous" } {
  const barcode = code.trim().toLowerCase();
  if (!barcode) return { status: "missing" };

  const matches: { product: T; variant?: VariantLabelRow }[] = [];
  for (const product of products) {
    if (product.sku?.trim().toLowerCase() === barcode) matches.push({ product });
    if (product.hasVariants) {
      const variants = parseLabelVariants(product.variants);
      for (const variant of variants ?? []) {
        if (variant.sku && variant.sku.toLowerCase() === barcode) matches.push({ product, variant });
      }
    }
  }
  if (matches.length === 0) return { status: "missing" };
  if (matches.length > 1) return { status: "ambiguous" };
  return { status: "found", ...matches[0] };
}